import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpRequestErrorHandler} from '@rbaip/ui-component';
import {catchError} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {SellerLocation} from '../domain/item';
import {appConfig} from '../app.config';
import {AssetPackageDto, AssetControlGroupSearchCriteria, AssetPackageSearchResultDto} from '../domain/asset-package';

@Injectable({
  providedIn: 'root'
})
export class ItemSummaryAssetControlGroupService {

  getAssetPackageURL = '/api/asset-package/:acgId';
  assetControlGroupLocationsCallback: Subject<SellerLocation[]> = new Subject<SellerLocation[]>();
  assetControlGroupLocations: Array<SellerLocation>;

  private searchString: string;
  private filterColumnNameValue: Map<string, Array<string>>;
  private initiator: string;

  private readonly saveBudgetsUrl = '/api/asset-control-groups/:assetControlGroupId/budgets';
  private readonly saveDefaultsUrl = '/api/asset-control-groups/:assetControlGroupId/defaults';

  constructor(private http: HttpClient) {
    this.init();
  }


  private init(): void {
    this.searchString = '';
    this.filterColumnNameValue = new Map<string, Array<string>>();
    this.initiator = '';
  }

  public getAllAssetPackages(territoryManagerId: number): Observable<any> {
    let url = '/api/:territoryManagerId/assetPackages';
    url = url.replace(':territoryManagerId', String(territoryManagerId));
    return this.http.get<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('getAllAssetPackages', null)));
  }

  public getAllAssetPackage(searchQuery: AssetControlGroupSearchCriteria): Observable<AssetPackageSearchResultDto> {
    const url = '/api/asset-packages';
    const body = JSON.stringify(searchQuery);
    return this.http.post<AssetPackageSearchResultDto>(url, body, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('getAllAssetPackage', null)));
  }

  public getAssetPackageInfo(assetControlGroupId: number): Observable<AssetPackageDto> {
    let url = this.getAssetPackageURL;
    url = url.replace(':acgId', String(assetControlGroupId));
    return this.http.get<AssetPackageDto>(url, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('getAssetPackageInfo', {}))
      );
  }

  public saveBudgets(assetPackage: AssetPackageDto): Observable<any> {
    const url = this.saveBudgetsUrl.replace(':assetControlGroupId', String(assetPackage.acgId));
    return this.http.post<any>(url, assetPackage, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('saveBudgets', false))
      );
  }

  public saveDefaults(assetPackage: AssetPackageDto): Observable<any> {
    const url = this.saveDefaultsUrl.replace(':assetControlGroupId', String(assetPackage.acgId));
    return this.http.post<any>(url, assetPackage, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('saveDefaults', false))
      );
  }

  public setSearchString(searchString: string) {
    this.searchString = searchString;
  }

  public getSearchString(): string {
    return this.searchString;
  }

  public getInitiator(): string {
    return this.initiator;
  }

  public setInitiator(initiator: string) {
    this.initiator = initiator;
  }

  public addFilterColumnNameValue(name: string, value: string) {
    if (name && value) {
      let values = this.filterColumnNameValue.get(name);
      if (values) {
        values.push(value);
      } else {
        values = [value];
      }

      this.filterColumnNameValue.set(name, values);
    }
  }

  public getFilterColumnNameValue() {
    return this.filterColumnNameValue;
  }

  public clear(): void {
    this.init();
  }

  public loadAssetControlGroupsForAutoComplete(searchParam): Observable<any> {
    const url = '/api/asset-control-groups/auto-complete/search?searchParam=' + searchParam;
    return this.http.get<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('loadAssetControlGroupsForAutoComplete', null)));
  }

  public mergePackage(masterAcgId, orphanAcgId): Observable<any> {
    let url = '/api/asset-control-group/merge-asset-package/:assetControlGroupId?masterAcgId=' + masterAcgId;
    url = url.replace(':assetControlGroupId', orphanAcgId);


    const body = '';
    return this.http.put(url, body, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('mergePackage', null))
      );
  }

  public getLocationsByAssetControlGroup(assetControlGroupId: number): void {
    const url = '/api/storage-location?acgId=' + assetControlGroupId;

    this.http.get(url, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('getLocationsByAssetControlGroup', []))
      )
      .subscribe(response => {
        this.assetControlGroupLocations = response as SellerLocation[];
        this.assetControlGroupLocationsCallback.next(this.assetControlGroupLocations);
      });
  }

  public triggerACGAPIDispatch(opportunityId: number): Observable<any> {
    let url = '/api/trigger-acg-dispatch/:opportunityId';
    url = url.replace(':opportunityId', String(opportunityId));
    return this.http.post<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('triggerACGAPIDispatch', false))
    );
  }

  public isPackageTeamMember(assetPackageId: number, userId: number): Observable<any> {
    const url = `/api/acgId/${assetPackageId}/userId/${userId}/isCurrentUserIsTMorRM`;
    return this.http.get<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('isPackageTeamMember', false))
    );
  }

  public triggerOwnerCodeSync(assetControlGroupId: number): Observable<any> {
    let url = '/api/trigger-owner-code-sync/:assetControlGroupId';
    url = url.replace(':assetControlGroupId', String(assetControlGroupId));

    return this.http.post<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('triggerOwnerCodeSync', false))
    );
  }

  public checkMissingOwnerCode(assetControlGroupId: number): Observable<any> {
    let url = '/api/check-missing-owner-code/:assetControlGroupId';
    url = url.replace(':assetControlGroupId', String(assetControlGroupId));

    return this.http.post<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('checkMissingOwnerCode', false))
    );
  }

  public getPPSUrl(assetControlGroupId: number): Observable<any> {
    let url = '/api/get-pps-url/:assetControlGroupId';
    url = url.replace(':assetControlGroupId', String(assetControlGroupId));

    return this.http.get<any>(url, appConfig.httpOptions).pipe(
      catchError(HttpRequestErrorHandler.handleError('getPPSUrl', false))
    );
  }
}
